import type { CollectionDto } from "@serophin/nottldr-shared";
import { useCollections } from "@serophin/nottldr-shared/hooks";
import { Check, Copy, Pencil, Trash2, X } from "lucide-preact";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import { CopyCollectionModal } from "./CopyCollectionModal";
import { articleCount, errorMessage } from "./collectionText";
import { DeleteCollectionModal } from "./DeleteCollectionModal";

type Props = { collection: CollectionDto; allCollections: CollectionDto[] };

const ICON_BUTTON = "p-1 transition-colors disabled:opacity-50";

const useRename = (collection: CollectionDto) => {
  const toast = useToast();
  const { rename } = useCollections();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(collection.name);
  const [saving, setSaving] = useState(false);

  const start = () => {
    setName(collection.name);
    setEditing(true);
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Collection name cannot be empty");
      return;
    }
    if (trimmed === collection.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await rename(collection.id, trimmed);
      setEditing(false);
      toast.success("Collection renamed successfully!");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to rename collection"));
    } finally {
      setSaving(false);
    }
  };

  return { editing, name, saving, setName, start, save, cancel: () => setEditing(false) };
};

type Rename = ReturnType<typeof useRename>;

const EditName = ({ rename }: { rename: Rename }) => (
  <div class="flex items-center gap-2">
    <input
      type="text"
      value={rename.name}
      onInput={(event) => rename.setName(event.currentTarget.value)}
      disabled={rename.saving}
      class="flex-1 px-2 py-1 text-sm font-semibold border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-neutral-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      maxLength={50}
      autoFocus
    />
    <button
      type="button"
      onClick={rename.save}
      disabled={rename.saving}
      class={`${ICON_BUTTON} text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400`}
      aria-label="Save"
    >
      <Check class="w-5 h-5" />
    </button>
    <button
      type="button"
      onClick={rename.cancel}
      disabled={rename.saving}
      class={`${ICON_BUTTON} text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300`}
      aria-label="Cancel"
    >
      <X class="w-5 h-5" />
    </button>
  </div>
);

const NameLink = ({ collection, onEdit }: { collection: CollectionDto; onEdit: () => void }) => (
  <a href={collection.isDefault ? "/collections" : `/collections?id=${collection.id}`} class="block">
    <div class="flex items-center gap-2 min-w-0">
      <h3
        class="text-sm font-semibold text-gray-900 dark:text-white truncate hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        title={collection.name}
      >
        {collection.name}
      </h3>
      {collection.isDefault ? (
        <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-neutral-100 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-400 flex-shrink-0">
          Default
        </span>
      ) : (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            onEdit();
          }}
          class="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors flex-shrink-0"
          aria-label="Edit collection name"
        >
          <Pencil class="w-4 h-4" />
        </button>
      )}
    </div>
    <p class="text-xs text-gray-600 dark:text-gray-400">{articleCount(collection.articleCount)}</p>
  </a>
);

const Actions = ({ collection, onCopy, onDelete }: { collection: CollectionDto; onCopy: () => void; onDelete: () => void }) => (
  <div class="flex-shrink-0 flex items-center gap-1">
    {collection.articleCount > 0 && (
      <button
        type="button"
        onClick={onCopy}
        class="p-2 text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400 transition-colors"
        aria-label="Copy collection"
      >
        <Copy class="w-5 h-5" />
      </button>
    )}
    {!collection.isDefault && (
      <button
        type="button"
        onClick={onDelete}
        class="p-2 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
        aria-label="Delete collection"
      >
        <Trash2 class="w-5 h-5" />
      </button>
    )}
  </div>
);

export const CollectionCard = ({ collection, allCollections }: Props) => {
  const rename = useRename(collection);
  const [copyOpen, setCopyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const hasActions = collection.articleCount > 0 || !collection.isDefault;

  return (
    <>
      <div class="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between gap-3">
          <div class="flex-1 min-w-0">{rename.editing ? <EditName rename={rename} /> : <NameLink collection={collection} onEdit={rename.start} />}</div>
          {!rename.editing && hasActions && <Actions collection={collection} onCopy={() => setCopyOpen(true)} onDelete={() => setDeleteOpen(true)} />}
        </div>
      </div>
      <CopyCollectionModal isOpen={copyOpen} onClose={() => setCopyOpen(false)} collection={collection} allCollections={allCollections} />
      <DeleteCollectionModal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} collection={collection} allCollections={allCollections} />
    </>
  );
};
